import * as React from "react"

import { usePlatform } from "@/platform/PlatformProvider"

/** Tailwind `md` — phones in portrait */
export const MOBILE_BREAKPOINT = 768
/** Mobile tab shell vs desktop multi-panel layout */
export const TABLET_BREAKPOINT = 1024

function readIsDesktopViewport(): boolean {
  if (typeof window === "undefined") return true
  return window.innerWidth >= TABLET_BREAKPOINT
}

export function useIsMobile() {
  // Domyślnie zakładamy, że to nie jest mobile (lepsze dla SSR)
  const [isMobile, setIsMobile] = React.useState<boolean>(false)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return isMobile
}

export function useIsTablet() {
  const [isTablet, setIsTablet] = React.useState<boolean>(false)

  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px) and (max-width: ${TABLET_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsTablet(window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < TABLET_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsTablet(window.innerWidth >= MOBILE_BREAKPOINT && window.innerWidth < TABLET_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return isTablet
}

export function useIsDesktop() {
  const { platform } = usePlatform()
  const forceMobileShell = platform === "ios"
  const [isDesktop, setIsDesktop] = React.useState<boolean>(() =>
    forceMobileShell ? false : readIsDesktopViewport(),
  )

  React.useEffect(() => {
    if (forceMobileShell) {
      setIsDesktop(false)
      return
    }

    const mql = window.matchMedia(`(min-width: ${TABLET_BREAKPOINT}px)`)
    const onChange = () => {
      setIsDesktop(readIsDesktopViewport())
    }
    mql.addEventListener("change", onChange)
    setIsDesktop(readIsDesktopViewport())
    return () => mql.removeEventListener("change", onChange)
  }, [forceMobileShell])

  return isDesktop
}

export function useResponsive() {
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isDesktop = useIsDesktop()

  return {
    isMobile,
    isTablet,
    isDesktop,
    // Convenience getters
    isMobileOrTablet: isMobile || isTablet,
    isTabletOrDesktop: isTablet || isDesktop
  }
}

// Export as useMobile for compatibility
export const useMobile = useIsMobile
